import { useEffect, useState } from 'react';
import { Button, Dropdown, Offcanvas } from 'react-bootstrap';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import type { ModuleGroup } from '../api/types';

function useTheme() {
  const [theme, setTheme] = useState<string>(() => document.documentElement.getAttribute('data-bs-theme') ?? 'light');
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-bs-theme', next);
    try {
      localStorage.setItem('mms-theme', next);
    } catch {
      /* storage may be blocked; the theme still applies for this session */
    }
    setTheme(next);
  };
  return { theme, toggle };
}

function SidebarNav({ modules, onNavigate }: { modules: ModuleGroup[]; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  // Sections default to open for the module holding the current page; users can toggle the rest.
  const [closed, setClosed] = useState<Record<number, boolean>>({});
  return (
    <nav aria-label="Main">
      <ul className="nav flex-column mms-nav">
        <li className="nav-item">
          <NavLink to="/" end className="nav-link" onClick={onNavigate}>
            <i className="bi bi-speedometer2 me-2" aria-hidden />Dashboard
          </NavLink>
        </li>
        {modules.map((m) => {
          const holdsCurrent = m.menus.some((x) => pathname === x.path || pathname.startsWith(`${x.path}/`));
          const open = closed[m.id] === undefined ? true : !closed[m.id];
          return (
            <li key={m.id} className="nav-item mt-2">
              <button
                type="button"
                className={`mms-nav-section btn btn-link w-100 text-start d-flex align-items-center ${holdsCurrent ? 'active' : ''}`}
                aria-expanded={open}
                onClick={() => setClosed((c) => ({ ...c, [m.id]: open }))}
              >
                <i className={`bi bi-${m.icon ?? 'folder'} me-2`} aria-hidden />
                <span className="flex-grow-1">{m.name}</span>
                <i className={`bi bi-chevron-${open ? 'up' : 'down'} small`} aria-hidden />
              </button>
              {open && (
                <ul className="nav flex-column ms-3 border-start ps-2">
                  {m.menus.map((x) => (
                    <li key={x.code} className="nav-item">
                      <NavLink to={x.path} className="nav-link py-1" onClick={onNavigate}>{x.label}</NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function Layout() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const { theme, toggle } = useTheme();
  const [showMenu, setShowMenu] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setShowMenu(false), [pathname]);
  if (!me) return null;

  const doLogout = async () => {
    await logout();
    nav('/login', { replace: true });
  };

  return (
    <div className="mms-shell">
      <a href="#main" className="visually-hidden-focusable">Skip to content</a>
      <header className="mms-topbar navbar bg-body-tertiary border-bottom px-3 sticky-top">
        <div className="d-flex align-items-center gap-2">
          <Button variant="outline-secondary" size="sm" className="d-lg-none" onClick={() => setShowMenu(true)} aria-label="Open menu">
            <i className="bi bi-list" aria-hidden />
          </Button>
          <Link to="/" className="navbar-brand fw-bold mb-0 text-primary">MMS</Link>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button variant="link" className="text-body-secondary" onClick={toggle} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} title="Toggle theme">
            <i className={`bi ${theme === 'dark' ? 'bi-sun' : 'bi-moon-stars'}`} aria-hidden />
          </Button>
          <Dropdown align="end">
            <Dropdown.Toggle variant="outline-secondary" size="sm" id="user-menu">
              <i className="bi bi-person-circle me-1" aria-hidden />{me.user.name}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>{me.user.email}<br /><small>{me.user.role}</small></Dropdown.Header>
              <Dropdown.Item as={Link} to="/profile">Profile &amp; password</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={doLogout}>Sign out</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </header>

      <div className="mms-body">
        <aside className="mms-sidebar d-none d-lg-block border-end bg-body-tertiary p-3">
          <SidebarNav modules={me.modules} />
        </aside>
        {/* Mobile menu: only mounted while open, so the nav exists once in the DOM at any screen size */}
        <Offcanvas show={showMenu} onHide={() => setShowMenu(false)} className="d-lg-none">
          <Offcanvas.Header closeButton><Offcanvas.Title>Menu</Offcanvas.Title></Offcanvas.Header>
          <Offcanvas.Body><SidebarNav modules={me.modules} onNavigate={() => setShowMenu(false)} /></Offcanvas.Body>
        </Offcanvas>
        <main id="main" className="mms-main p-3 p-lg-4" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
